/*
 * <vitesse-shared-dashboard> — a board on its own.
 *
 * What a share's viewer sees: the frozen tiles on the same grid and the same
 * hosts the app uses, under a grant derived from the share's scope, with none
 * of the app around it — no tree, no search, no AI, no marketplace. The grid is
 * locked: a viewer rearranges nothing and removes nothing, and the layout is
 * never written anywhere.
 *
 * Reusing the app's own board pieces is what keeps this honest: a share cannot
 * render what the app cannot, and a bug fixed on a board is fixed here too.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { t } from '@vitesse/base-ui/i18n';
import { fmtDateTime } from '@vitesse/base-ui/format';
import { LocaleController } from '@vitesse/base-ui/i18n';
import { statusBadge } from '@vitesse/base-ui/components/templates';
import type { BackendClient } from '../bridge/backend-client';
import type { ModelManager } from '../managers/model-manager';
import type { OpenShareWire } from '../../core/bridge/connect-client';
import type { CapabilityGrant } from '../../dashboards/runtime/capability';
import type { PanelDef, PanelLayoutItem } from './vitesse-panel-grid';
import './vitesse-panel-grid';
import '../../dashboards/widget-host';
import '@vitesse/base-ui/elements/vitesse-logo';

@customElement('vitesse-shared-dashboard')
export class VitesseSharedDashboard extends LitElement {
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  @property({ attribute: false }) share?: OpenShareWire;
  @property({ attribute: false }) grant!: CapabilityGrant;
  @property({ attribute: false }) backend?: BackendClient;
  @property({ attribute: false }) models?: ModelManager;

  constructor() {
    super();
    new LocaleController(this);
  }

  private get panels(): PanelDef[] {
    return (this.share?.tiles ?? []).map((tile) => ({ id: tile.id, title: tile.title, noMove: true, noResize: true }));
  }
  private get layout(): PanelLayoutItem[] {
    return (this.share?.tiles ?? []).map((tile) => ({ id: tile.id, ...tile.layout }));
  }

  render() {
    const s = this.share;
    if (!s) return nothing;
    const expires = s.expiresAtMs ? fmtDateTime(s.expiresAtMs) : '';
    return html`<div class="shared-board">
      <header class="shared-board-head">
        <vitesse-logo size="20"></vitesse-logo>
        <h1 class="shared-board-title">${s.title}</h1>
        <div class="shared-board-badges">
          ${statusBadge('green', t('dashboards.sharedLive'))}
          ${expires ? statusBadge('gray', t('dashboards.shareExpires', { when: expires })) : nothing}
          ${s.allowInteraction ? statusBadge('blue', t('dashboards.shareCanAct')) : statusBadge('gray', t('dashboards.sharedViewOnly'))}
        </div>
      </header>
      <div class="vg-wrapper dashboard-canvas shared-board-canvas">
        <vitesse-panel-grid .panels=${this.panels} .layout=${this.layout} locked>
          ${repeat(
            s.tiles,
            (tile) => tile.id,
            (tile) => html`<vitesse-widget-host
              slot=${tile.id}
              .source=${tile.source}
              .config=${tile.config}
              .grant=${this.grant}
              .backend=${this.backend}
              .gatewayId=${s.gatewayId}
              .boardKey=${'share'}
              .models=${this.models}
            ></vitesse-widget-host>`,
          )}
        </vitesse-panel-grid>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-shared-dashboard': VitesseSharedDashboard;
  }
}
